import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Bot, Zap, FileText, Crown } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export const CurrentPlan = () => {
  const { data: currentPlan, isLoading } = useQuery({
    queryKey: ['currentPlan'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.warn('[CurrentPlan] No active session found');
        return null;
      }

      console.log('[CurrentPlan] Fetching plan for user:', session.user.id);
      
      const { data, error } = await supabase.rpc('get_user_plan', {
        p_user_id: session.user.id
      });
      
      if (error) {
        console.error('[CurrentPlan] Error fetching user plan:', error);
        throw error;
      }

      console.log('[CurrentPlan] Retrieved plan data:', data);
      return data[0];
    },
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </Card>
    );
  }

  if (!currentPlan) {
    return null;
  }

  const planName = currentPlan.plan_type?.charAt(0).toUpperCase() + currentPlan.plan_type?.slice(1);

  const features = [
    {
      name: "Monthly Tokens",
      icon: Crown,
      value: currentPlan.monthly_tokens,
      type: "tokens"
    },
    {
      name: "Email Accounts",
      icon: Mail,
      current: 0, // This would need to be fetched from email_accounts table
      limit: currentPlan.email_accounts_limit,
      unlimited: !currentPlan.email_accounts_limit
    },
    {
      name: "AI Assistants",
      icon: Bot,
      current: 0, // This would need to be fetched from assistants table
      limit: currentPlan.assistants_limit,
      unlimited: !currentPlan.assistants_limit
    },
    {
      name: "Bulk Emails",
      icon: FileText,
      limit: currentPlan.bulk_emails_limit,
      unlimited: !currentPlan.bulk_emails_limit,
      current: 0 // This is per-operation
    },
    {
      name: "Model Access",
      icon: Zap,
      type: "feature",
      value: currentPlan.allows_extended_model ? "Extended & Efficient" : "Efficient only"
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Crown className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Current Plan</h3>
        <Badge variant={currentPlan.plan_type === 'free' ? 'secondary' : 'default'}>
          {planName}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {features.map((feature) => (
          <Card key={feature.name} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <feature.icon className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium">{feature.name}</h4>
              </div>
              {'unlimited' in feature && (
                <Badge variant={feature.unlimited ? "default" : "secondary"}>
                  {feature.unlimited ? "Unlimited" : `${feature.current}/${feature.limit}`}
                </Badge>
              )}
              {'type' in feature && feature.type === 'tokens' && (
                <Badge variant="default">
                  {feature.value}
                </Badge>
              )}
            </div>
            {'limit' in feature && !feature.unlimited && (
              <Progress 
                value={(feature.current / (feature.limit || 1)) * 100} 
                className="h-2"
              />
            )}
            {'type' in feature && feature.type === 'feature' && (
              <p className="text-sm text-muted-foreground mt-1">{feature.value}</p>
            )}
          </Card>
        ))}
      </div>

      {currentPlan.requires_signature && (
        <p className="text-sm text-muted-foreground mt-2">
          Includes Maia signature in emails
        </p>
      )}
    </div>
  );
};